import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, CheckCircle, XCircle, Clock, Trash2, Users, Calendar } from "lucide-react";
import { toast } from "sonner";
import supabase from "@/lib/supabase";

const statusConfig = {
  pending: { label: "Pendiente", color: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30" },
  confirmed: { label: "Confirmada", color: "text-green-400 bg-green-400/10 border-green-400/30" },
  cancelled: { label: "Cancelada", color: "text-red-400 bg-red-400/10 border-red-400/30" },
};

const formatDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};

// ---- Login Screen ----
const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 400)); // small delay for UX
    if (password === process.env.REACT_APP_ADMIN_PASSWORD) {
      localStorage.setItem("neli_admin_auth", "true");
      onLogin();
      toast.success("Bienvenido, Admin");
    } else {
      toast.error("Contraseña incorrecta");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <span className="font-heading text-4xl font-bold text-gold tracking-wider">NELI</span>
          <p className="text-[10px] tracking-[0.3em] text-[#a3a3a3] uppercase mt-1">Panel de Administración</p>
        </div>
        <div className="bg-[#1c1c1c] border border-[#262626] p-8">
          <div className="h-px w-12 bg-gold mb-8" />
          <h2 className="font-heading text-2xl text-[#ededed] mb-6">Acceso restringido</h2>
          <form onSubmit={handleLogin} data-testid="admin-login-form" className="space-y-6">
            <div>
              <label className="text-gold text-xs tracking-widest uppercase block mb-2 font-body">Contraseña</label>
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full bg-[#262626] border-b border-[#333] focus:border-gold outline-none px-4 py-3 text-[#ededed] text-sm placeholder:text-[#555] transition-all duration-300"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full bg-gold text-black font-bold text-xs tracking-widest uppercase py-4 rounded-[20px] hover:bg-gold-dark transition-all duration-300 disabled:opacity-60"
            >
              {loading ? "Entrando..." : "Entrar"}
            </button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-[#a3a3a3] hover:text-gold text-xs transition-colors">
              Volver al sitio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Dashboard ----
const Dashboard = ({ onLogout }) => {
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, cancelled: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('reservas')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setReservations(data || []);
      const total = data.length;
      const pending = data.filter((r) => r.status === 'pending').length;
      const confirmed = data.filter((r) => r.status === 'confirmed').length;
      const cancelled = data.filter((r) => r.status === 'cancelled').length;
      setStats({ total, pending, confirmed, cancelled });
    } catch (err) {
      toast.error("Error al cargar las reservas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, status) => {
    try {
      const { error } = await supabase
        .from('reservas')
        .update({ status })
        .eq('id', id);
      if (error) throw error;
      toast.success(`Reserva ${status === 'confirmed' ? 'confirmada' : 'cancelada'}`);
      fetchData();
    } catch {
      toast.error("Error al actualizar la reserva");
    }
  };

  const deleteReservation = async (id) => {
    if (!window.confirm("¿Eliminar esta reserva?")) return;
    try {
      const { error } = await supabase.from('reservas').delete().eq('id', id);
      if (error) throw error;
      toast.success("Reserva eliminada");
      fetchData();
    } catch {
      toast.error("Error al eliminar la reserva");
    }
  };

  const filtered = filter === "all" ? reservations : reservations.filter((r) => r.status === filter);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="admin-dashboard">
      {/* Top Bar */}
      <div className="bg-[#080808] border-b border-[#1c1c1c] px-6 md:px-12 py-5 flex items-center justify-between sticky top-0 z-40">
        <div>
          <span className="font-heading text-xl font-bold text-gold tracking-wider">NELI</span>
          <span className="text-[#a3a3a3] text-xs tracking-widest uppercase ml-3">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-[#a3a3a3] hover:text-gold text-xs tracking-widest uppercase transition-colors">
            Ver sitio
          </Link>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-[#a3a3a3] hover:text-red-400 text-xs tracking-widest uppercase transition-colors"
          >
            <LogOut size={14} />
            Salir
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: "Total", value: stats.total, color: "border-gold/30", icon: Calendar, testid: "stats-total" },
            { label: "Pendientes", value: stats.pending, color: "border-yellow-400/30", icon: Clock, testid: "stats-pending" },
            { label: "Confirmadas", value: stats.confirmed, color: "border-green-400/30", icon: CheckCircle, testid: "stats-confirmed" },
            { label: "Canceladas", value: stats.cancelled, color: "border-red-400/30", icon: XCircle, testid: "stats-cancelled" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className={`bg-[#1c1c1c] border ${s.color} p-6`} data-testid={s.testid}>
                <div className="flex items-start justify-between mb-3">
                  <span className="text-[#a3a3a3] text-xs tracking-widest uppercase font-body">{s.label}</span>
                  <Icon size={16} className="text-[#a3a3a3]" />
                </div>
                <span className="font-heading text-3xl text-[#ededed] font-bold">{s.value}</span>
              </div>
            );
          })}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-0 border-b border-[#262626] mb-8">
          {[
            { key: "all", label: "Todas" },
            { key: "pending", label: "Pendientes" },
            { key: "confirmed", label: "Confirmadas" },
            { key: "cancelled", label: "Canceladas" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              data-testid={`filter-${tab.key}`}
              className={`px-5 py-3 text-xs tracking-widest uppercase font-body transition-all border-b-2 -mb-px ${
                filter === tab.key
                  ? "text-gold border-gold"
                  : "text-[#a3a3a3] border-transparent hover:text-[#ededed]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-20 text-[#a3a3a3]">Cargando reservas...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#a3a3a3]">No hay reservas en esta categoría.</div>
        ) : (
          <div className="overflow-x-auto" data-testid="reservation-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#262626]">
                  {["Nombre", "Contacto", "Fecha", "Hora", "Personas", "Estado", "Acciones"].map((h) => (
                    <th key={h} className="text-left text-[#a3a3a3] text-xs tracking-widest uppercase pb-4 pr-4 font-body">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1c1c1c]">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-[#111] transition-colors" data-testid={`reservation-row-${r.id}`}>
                    <td className="py-4 pr-4">
                      <div className="font-body text-[#ededed] font-medium">{r.name}</div>
                      {r.comments && (
                        <div className="text-[#555] text-xs mt-0.5 truncate max-w-[150px]">{r.comments}</div>
                      )}
                    </td>
                    <td className="py-4 pr-4">
                      <div className="font-body text-[#a3a3a3] text-xs">{r.email}</div>
                      <div className="font-body text-[#a3a3a3] text-xs">{r.phone}</div>
                    </td>
                    <td className="py-4 pr-4 font-body text-[#a3a3a3] text-xs whitespace-nowrap">
                      {formatDate(r.date)}
                    </td>
                    <td className="py-4 pr-4 font-body text-[#a3a3a3] text-xs">{r.time}</td>
                    <td className="py-4 pr-4">
                      <div className="flex items-center gap-1.5 text-[#a3a3a3] text-xs">
                        <Users size={12} />
                        {r.persons}
                      </div>
                    </td>
                    <td className="py-4 pr-4">
                      <span className={`text-xs px-2.5 py-1 border font-body ${statusConfig[r.status]?.color}`}>
                        {statusConfig[r.status]?.label}
                      </span>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        {r.status === "pending" && (
                          <>
                            <button
                              data-testid={`confirm-btn-${r.id}`}
                              onClick={() => updateStatus(r.id, "confirmed")}
                              className="p-1.5 text-green-400 hover:bg-green-400/10 transition-colors"
                              title="Confirmar"
                            >
                              <CheckCircle size={16} />
                            </button>
                            <button
                              data-testid={`cancel-btn-${r.id}`}
                              onClick={() => updateStatus(r.id, "cancelled")}
                              className="p-1.5 text-yellow-400 hover:bg-yellow-400/10 transition-colors"
                              title="Cancelar"
                            >
                              <XCircle size={16} />
                            </button>
                          </>
                        )}
                        <button
                          data-testid={`delete-btn-${r.id}`}
                          onClick={() => deleteReservation(r.id)}
                          className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Main Admin Component ----
const Admin = () => {
  const [auth, setAuth] = useState(() => localStorage.getItem("neli_admin_auth") === "true");

  const handleLogin = () => setAuth(true);
  const handleLogout = () => {
    localStorage.removeItem("neli_admin_auth");
    setAuth(false);
  };

  if (!auth) return <LoginScreen onLogin={handleLogin} />;
  return <Dashboard onLogout={handleLogout} />;
};

export default Admin;

